import React, { useRef, useEffect, useState } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { useSpring, animated, config } from '@react-spring/web';
import abnamro from "../assets/images/companies/abn_amro.png";
import rabobank from "../assets/images/companies/rabobank.png";
import spliethoff from "../assets/images/companies/spliethoff.png";
import ect from "../assets/images/companies/ect.png";
import jumbo from "../assets/images/companies/jumbo.png";
import * as THREE from 'three';
import { Link, useNavigate } from "react-router-dom";

function HeroSection() {
    const isMobileView = window.innerWidth <= 768; // Perform the check once

    const navigate = useNavigate();

    const subTitleAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(-50px)' },
        to: { opacity: 1, transform: 'translateY(0px)' },
        delay: 500,
        config: config.slow,
    });

    const titleAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(-50px)' },
        to: { opacity: 1, transform: 'translateY(0px)' },
        delay: 200,
        config: config.slow,
    });

    const descriptionAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(-50px)' },
        to: { opacity: 1, transform: 'translateY(0px)' },
        delay: 600,
        config: config.slow,
    });

    const buttonAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(-50px)' },
        to: { opacity: 1, transform: 'translateY(0px)' },
        delay: 1000,
        config: config.slow,
    });

    const trustedByAnimation = useSpring({
        from: { opacity: 0, transform: 'translateY(-50px)' },
        to: { opacity: 1, transform: 'translateY(0px)' },
        delay: 1400,
        config: config.slow,
    });

    // Subtitle switching logic
    const subtitleList = ["Lakehouse", "Governance", "Dashboards", "Insights"];
    const [subtitleIndex, setSubtitleIndex] = useState(0);
    const [showSubtitle, setShowSubtitle] = useState(true);

    useEffect(() => {
        const interval = setInterval(() => {
            setShowSubtitle(false); // trigger exit
            setTimeout(() => {
                setSubtitleIndex((prev) => (prev + 1) % subtitleList.length);
                setShowSubtitle(true); // trigger enter
            }, 200); // match animation duration
        }, 2000);
        return () => clearInterval(interval);
    }, [subtitleList.length]);

    const scrollToSection = (sectionName) => {
        const formattedId = sectionName.toLowerCase().replace(" ", "-");
        const section = document.getElementById(formattedId);

        if (section) {
            section.scrollIntoView({ behavior: "smooth" });
        }
    };

    const hoverButton = (e) => {
        e.target.style.backgroundColor = '#00BCD4';
        e.target.style.transform = 'scale(1.05)';
    };

    const resetButton = (e) => {
        e.target.style.backgroundColor = 'black';
        e.target.style.transform = 'scale(1)';
    };

    const subTitleStyles = {
        fontFamily: 'Inter, sans-serif',
        fontSize: isMobileView ? '0.8rem' : '1.5rem', // Reduced from 1.8rem
        fontWeight: 200,
        maxWidth: '900px',
        margin: '0 auto 1rem',
        color: '#000000',
    };

    const titleStyles = {
        fontFamily: 'Inter, sans-serif',
        fontSize: isMobileView ? '2rem' : '3.4rem', // Reduced from 3.8rem
        fontWeight: 400,
        maxWidth: '900px',
        margin: '0 auto 1rem',
        color: '#000000',
    };

    const descriptionStyles = {
        fontFamily: 'Inter, sans-serif',
        fontSize: isMobileView ? '1rem' : '1.2rem', // Reduced from 1.3rem
        fontWeight: 300,
        maxWidth: '800px',
        margin: '0 auto 2rem',
        color: '#374151',
        lineHeight: 1.6,
        textAlign: 'center',
    };

    const buttonStyles = {
        backgroundColor: 'black',
        color: '#fff',
        padding: isMobileView ? '10px 20px' : '15px 30px',
        borderRadius: '8px',
        fontSize: isMobileView ? 'clamp(0.9rem, 2.5vw, 1rem)' : 'clamp(0.9rem, 2.5vw, 1rem)',
        cursor: 'pointer',
    };

    const trustedByStyles = {
        marginTop: isMobileView ? '4rem' : '6rem', // Reduced from 8rem
        textAlign: 'center',
        color: '#374151',
    };

    const companyListStyles = {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        maxWidth: '60rem',
        gap: isMobileView ? '0.8rem' : '1.1rem',
    };

    const trustedByTitleStyles = {
        fontFamily: 'Inter, sans-serif',
        fontSize: isMobileView ? '0.6rem' : '1.2rem', // Reduced from 1.4rem
        fontWeight: 300,
        maxWidth: '60rem',
        margin: isMobileView ? '0 0 0.7rem 0' : undefined,
    };

    const companyLogoStyles = {
        width: isMobileView ? '70px' : '100px', // Reduced from 120px
        objectFit: 'contain',
        filter: 'grayscale(1) brightness(0.5) opacity(0.5)', // transparent grey effect
        transition: 'filter 0.3s',
        margin: isMobileView ? '0 2px' : '0 8px',
    };

    return (
        <div style={heroSectionStyles}>
            {/* <Canvas style={canvasStyles} dpr={[1, 1.5]} frameloop="demand">
                <WaveLines />
            </Canvas> */}

            <animated.div style={{ ...contentStyles }}>
                {/* Animated subtitle with slide down effect */}
                <div style={{ height: isMobileView ? '2.2rem' : '2.8rem', marginBottom: '0.5rem', overflow: 'hidden', display: 'flex', justifyContent: 'center' }}>
                    <span
                        key={subtitleIndex}
                        style={{
                            ...subTitleStyles,
                            margin: 0,
                            display: 'inline-block',
                            opacity: showSubtitle ? 1 : 0,
                            animation: showSubtitle
                                ? 'slideDownSubtitle 0.3s cubic-bezier(.4,0,.2,1)'
                                : 'none',
                            transition: 'opacity 0.2s',
                        }}
                    >
                        {subtitleList[subtitleIndex]}
                    </span>
                </div>
                <animated.h1 style={{ ...titleStyles, ...titleAnimation }}>
                    Your AI Data Lakehouse Platform deployed <span style={{color: "#6666ff"}}>40x faster.</span>
                </animated.h1>
                <animated.p style={{ ...descriptionStyles, ...descriptionAnimation }}>
                    Skip the Chaos. Deploy an AI-Powered Data Lakehouse Platform in just <span style={{ color: "#6666ff", fontWeight: 'bold'}}>one day</span>
                    —Secure, Scalable, and always up‑to‑date —so you can focus on Insights.
                </animated.p>
                <animated.button
                    style={{ ...buttonStyles, ...buttonAnimation }}
                    onMouseOver={hoverButton}
                    onMouseOut={resetButton}
                    onClick={() => navigate("/contact")}
                >
                    Contact Us
                </animated.button>
                <animated.div style={{ ...trustedByStyles, ...trustedByAnimation }}>
                    <h2 className="trusted-by-title" style={trustedByTitleStyles}>
                        Partners from small and large Enterprises who are already trusting our Services
                    </h2>
                    <div style={companyListStyles}>
                        {Object.entries(companyLogos).map(([company, logoUrl], index) => (
                            <img
                                key={index}
                                src={logoUrl}
                                alt={`${company}`}
                                style={companyLogoStyles}
                            />
                        ))}
                    </div>
                </animated.div>
            </animated.div>
        </div>
    );
}

function WaveLines() {
    const groupRef = useRef();
    const { invalidate } = useThree();
    const lineCount = 10;
    const pointsPerLine = 30;

    useEffect(() => {
        const group = groupRef.current;
        for (let i = 0; i < lineCount; i++) {
            const points = [];
            for (let j = 0; j < pointsPerLine; j++) {
                const x = (j / pointsPerLine - 0.5) * 10;
                const y = Math.sin(j * 0.2) * 0.5;
                const z = i * 0.2 - (lineCount * 0.2) / 2;
                points.push(new THREE.Vector3(x, y, z));
            }
            const geometry = new THREE.BufferGeometry().setFromPoints(points);
            const material = new THREE.LineBasicMaterial({
                color: new THREE.Color('#00BCD4'),
                transparent: true,
                opacity: 0.6,
            });
            const line = new THREE.Line(geometry, material);
            group.add(line);
        }
    }, [lineCount, pointsPerLine]);

    useEffect(() => {
        const interval = setInterval(() => {
            groupRef.current.children.forEach((line, index) => {
                const positions = line.geometry.attributes.position.array;
                const time = performance.now() * 0.001;
                for (let i = 0; i < positions.length / 3; i++) {
                    const x = positions[i * 3];
                    positions[i * 3 + 1] = Math.sin(x * 0.5 + time + index * 0.2) * 0.5;
                }
                line.geometry.attributes.position.needsUpdate = true;
            });
            invalidate();
        }, 100);
        return () => clearInterval(interval);
    }, [invalidate]);

    return <group ref={groupRef} />;
}

const heroSectionStyles = {
    position: 'relative',
    height: '100vh',
    overflow: 'hidden',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    paddingTop: window.innerWidth <= 768 ? '0' : '4rem', // Removed padding completely on mobile
    marginTop: window.innerWidth <= 768 ? '-70px' : '0', // Increased negative margin from -10px to -30px
};

const canvasStyles = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
};

const contentStyles = {
    position: 'relative',
    textAlign: 'center',
    padding: window.innerWidth <= 768 ? '0 20px' : '20px', // Removed top/bottom padding on mobile
    maxWidth: '90%',
};

const ratingStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1rem',
    fontSize: '1rem',
    color: '#374151',
};

const starStyles = {
    display: 'inline-block',
    color: '#667',
    marginRight: '0.3rem',
};

const ratingTextStyles = {
    marginLeft: '0.5rem',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 300,
};

// Remove the duplicate definitions at the bottom of the file

const companyLogos = {
    "Spliethoff": spliethoff,
    "Hutchison Ports ECT": ect,
    "ABN AMRO": abnamro,
    "Rabobank": rabobank,
};

export default HeroSection;

// Add this CSS animation globally (e.g. in index.css or via a <style> tag in your app root):
/*
@keyframes slideDownSubtitle {
  from {
    opacity: 0;
    transform: translateY(-30%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
*/
